import * as React from 'react';
import { connect } from 'react-redux';
import { formatDate, getPlayerLink, truncateCurrencyNumber } from '../utils';
import { BaseElement, BaseCheaterContainer, Table } from '../components';
import {
    CalendarMonth,
} from '@mui/icons-material';
import { ContainerContext, mapProps } from './index';
import { Alert, Chip, Grid2 as Grid } from '@mui/material';
import { ActionRequest } from '../actions';
import { useNavigate } from 'react-router';
import { StateMap } from '../reducers';
import useMediaQuery from '@mui/material/useMediaQuery';
import { CheatersFilter } from '../components/cheaters/CheatersFilter';
import { Moment } from 'moment';

interface Props extends ContainerContext.Props {
}

interface State {
    readonly before: Moment|null;
    readonly after: Moment|null;
}

class Container extends BaseElement<Props, State> {
    state: State = {
        before: null,
        after: null,
    };

    protected renderContainer(): React.JSX.Element {
        return (
            <BaseCheaterContainer
                {...this.props}
                styled={this.props.styled}
                title = {"Cheaters"}
                showSubTabs
            >
                <Alert severity="info">
                    The values displayed here are a snapshot from when the player was flagged, intended to record the
                    reason for the banned. If a cheater continues to play, the values here may not reflect current
                    values.
                </Alert>
                {this.renderContent()}
            </BaseCheaterContainer>
        );
    }

    private renderContent = () => {
        const {
            before,
            after,
        } = this.state;

        const tableRef = React.createRef();

        const onSearch = () => {
            // @ts-ignore
            tableRef.current.retry();
        };

        const onFilter = (
            before: Moment|null,
            after: Moment|null,
        ) => {
            this.setState({
                before,
                after,
            });
            onSearch();
        };

        const onResetBefore = () => {
            this.setState({ before: null });
            onSearch();
        };
        const onResetAfter = () => {
            this.setState({ after: null });
            onSearch();
        };

        const columns = [
            { title: 'Player ID', field: 'playerId' },
            { title: 'VTD', field: 'valueToDateUSD', render: (rowData: any) => truncateCurrencyNumber(rowData.valueToDateUSD, 'USD') },
            { title: 'Flag Date', field: 'flagDate', render: (rowData: any) => formatDate(rowData.flagDate) },
            { title: 'Stage', field: 'stage' },
            { title: 'Gems', field: 'gems' },
            { title: 'Lipsticks', field: 'lipsticks' },
            { title: 'RuBadges', field: 'ru_badges' },
            { title: 'RuDollars', field: 'rudollars' },
            { title: 'Toot Coins', field: 'toot_coins' },
            { title: 'Voting Tickets', field: 'voting_tickets' },
            { title: 'Wild Cards', field: 'wild_cards' },
        ];

        const showChips = before != null || after != null;

        const title = (
            <Grid container={true} spacing={1}>
                <Grid size={12}>
                    <CheatersFilter
                        styled={this.props.styled}
                        before={before}
                        after={after}
                        onFilter={onFilter}
                    />
                </Grid>
                {showChips &&
                    <Grid size={12}>
                        <div style={{ display: 'flex', justifyContent: 'flex-start', flexWrap: 'wrap', gridColumnGap: 5, gridGap: 5}} >
                            {before !== null && (
                                <Chip
                                    size={'small'}
                                    icon={<CalendarMonth />}
                                    label={'Before:' + formatDate(before.valueOf())}
                                    onDelete={onResetBefore}
                                />
                            )}
                            {after !== null && (
                                <Chip
                                    size={'small'}
                                    icon={<CalendarMonth />}
                                    label={'After: ' + formatDate(after.valueOf())}
                                    onDelete={onResetAfter}
                                />
                            )}
                        </div>
                    </Grid>
                }
            </Grid>
        );

        const onRowClick = (event: any, rowData: any) => this.toLink(`/players/${rowData.playerId}/overview`);

        return (
            <Table
                styled={this.props.styled}
                tableRef={tableRef}
                columns={columns}
                title={title}
                onRowClick={onRowClick}
                options={{
                    selection: false,
                    paging: true,
                    pageSize: 10,
                    pageSizeOptions: [5, 10, 30, 50, 100],
                    emptyRowsWhenPaging: false,
                    search: true,
                    sorting: true,
                    draggable: false,
                }}
                data={this.getCheaters}
            />
        );
    };

    private readonly getCheaters = async (query: any) => {
        const {
            before,
            after,
        } = this.state;
        const { search, page, pageSize, orderBy, orderDirection } = query;

        const result = await ActionRequest.get<{cheaters: Entities.Cheater[], count: number}>(`cheaters/list`, {
            search,
            before: before?.valueOf(),
            after: after?.valueOf(),
            page: page + 1,
            perPage: pageSize,
            orderBy: orderBy ? orderBy.field : null,
            orderDirection: orderDirection === "" ? null : orderDirection,
        });
        if (!result) {
            return {
                data: [],
                page: 0,
                totalCount: 0,
            };
        }

        const data = result.cheaters.map((c: any) => {
            return {
                details: getPlayerLink(c.playerId),
                ...c,
            };
        });

        return {
            data,
            page: page,
            totalCount: result.count,
        };
    };
}

const mapStateToProps = (state: StateMap): Props => ({
    ...mapProps(state),
});
const AppContainer = (props: Props) =>
{
    const smallUp = useMediaQuery((theme) => theme.breakpoints.up('sm'));
    const navigate = useNavigate();
    return (<Container {...props} styled={{themeMode: props.app.themeMode, smallUp}} navigate={navigate}/>);
};
export default connect(mapStateToProps)(AppContainer);
